import { useState } from "react";
import { DndContext, closestCenter, type DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  BookOpen,
  ExternalLink,
  GripVertical,
  Link2,
  MoreVertical,
  Plug,
  RefreshCw,
  Search,
  Unlink,
} from "lucide-react";
import type { NotionCandidateDTO, PartnerKbPageDTO } from "@colab/shared";
import { useSortableSensors } from "@/lib/dnd-sensors";
import { cn, formatRelative } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/shared/empty-state";
import { NotionBlocks, NotionProperties } from "./notion-blocks";
import {
  useAddPartnerKbPage,
  useIntegrationKbCandidates,
  usePartnerIntegrationKb,
  useRefreshPartnerIntegrationKb,
  useRemovePartnerKbPage,
  useReorderPartnerKbPages,
} from "./use-partner-integration-kb";

// Internal-only "Knowledge Base" tab. Lives under /admin (rbac internal), so it
// is never reachable by a partner. Shows every Notion page linked to this
// partner, live-rendered and stacked, drag-to-reorder; lets staff link / unlink.

export function IntegrationsTab({ partnerId }: { partnerId: string }) {
  const query = usePartnerIntegrationKb(partnerId);
  const add = useAddPartnerKbPage(partnerId);
  const remove = useRemovePartnerKbPage(partnerId);
  const refresh = useRefreshPartnerIntegrationKb(partnerId);
  const reorder = useReorderPartnerKbPages(partnerId);
  const [pickerOpen, setPickerOpen] = useState(false);

  const sensors = useSortableSensors();

  if (query.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-2/3 rounded-lg" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <EmptyState
        icon={Plug}
        title="Couldn't load the Knowledge Base"
        description={query.error?.message ?? "Try refreshing the page."}
      />
    );
  }

  const pages = query.data.pages;

  const picker = (
    <LinkPickerDialog
      open={pickerOpen}
      onOpenChange={setPickerOpen}
      linking={add.isPending}
      onPick={(candidate) =>
        add.mutate({ pageId: candidate.id, pageUrl: candidate.url || undefined })
      }
    />
  );

  if (pages.length === 0) {
    return (
      <>
        <EmptyState
          icon={BookOpen}
          title="No Knowledge Base pages linked"
          description="Link the Notion page(s) that document this partner's integration. Visible to your team only — never shown to the partner."
          action={
            <Button onClick={() => setPickerOpen(true)}>
              <Link2 className="mr-1.5 h-4 w-4" />
              Link Notion page
            </Button>
          }
        />
        {picker}
      </>
    );
  }

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = pages.findIndex((p) => p.linkId === active.id);
    const newIndex = pages.findIndex((p) => p.linkId === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(pages, oldIndex, newIndex);
    reorder.mutate(next.map((p) => p.linkId));
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-3 border-b border-border/60 pb-3">
        <h2 className="font-display text-lg font-semibold tracking-tight">
          Knowledge Base
          <span className="ml-2 text-sm font-normal text-muted-foreground">
            {pages.length} page{pages.length === 1 ? "" : "s"}
          </span>
        </h2>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            title="Re-pull all pages from Notion"
          >
            <RefreshCw className={cn("h-4 w-4", refresh.isPending && "animate-spin")} />
          </Button>
          <Button size="sm" onClick={() => setPickerOpen(true)}>
            <Link2 className="mr-1.5 h-4 w-4" />
            Link page
          </Button>
        </div>
      </header>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext
          items={pages.map((p) => p.linkId)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-4">
            {pages.map((page) => (
              <SortableKbPage
                key={page.linkId}
                page={page}
                onUnlink={() => remove.mutate(page.linkId)}
                removing={remove.isPending}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {picker}
    </div>
  );
}

function SortableKbPage({
  page,
  onUnlink,
  removing,
}: {
  page: PartnerKbPageDTO;
  onUnlink: () => void;
  removing: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: page.linkId });
  const content = page.page;

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "rounded-xl border border-border/60 bg-card/40 p-4",
        isDragging && "opacity-60 shadow-lg",
      )}
    >
      <header className="flex items-start justify-between gap-3 border-b border-border/40 pb-2">
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            className="cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
          {content?.icon && !content.icon.startsWith("http") ? (
            <span aria-hidden>{content.icon}</span>
          ) : (
            <BookOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <span className="min-w-0">
            <span className="block truncate font-display text-base font-semibold tracking-tight">
              {content?.title ?? "Linked Notion page"}
            </span>
            <span className="text-xs text-muted-foreground">
              {page.linkedAt ? `Linked ${formatRelative(page.linkedAt)}` : "Linked"}
              {content?.fetchedAt ? ` · synced ${formatRelative(content.fetchedAt)}` : ""}
            </span>
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {page.pageUrl && (
            <Button variant="outline" size="sm" asChild>
              <a href={page.pageUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="mr-1.5 h-4 w-4" />
                Open
              </a>
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={onUnlink}
                disabled={removing}
                className="text-destructive focus:text-destructive"
              >
                <Unlink className="mr-2 h-4 w-4" />
                Unlink
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {content ? (
        <div className="pt-3">
          <NotionProperties properties={content.properties} />
          {content.blocks.length > 0 ? (
            <NotionBlocks blocks={content.blocks} />
          ) : content.properties.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              This Notion page has no content yet.
            </p>
          ) : null}
          {content.truncated && (
            <p className="mt-3 rounded border border-dashed border-border/60 px-3 py-2 text-xs text-muted-foreground">
              This page is long — only the first part is shown here. Open it in
              Notion to read the rest.
            </p>
          )}
        </div>
      ) : (
        <p className="pt-3 text-sm text-muted-foreground">
          {page.error ?? "The Notion page couldn't be loaded."}
        </p>
      )}
    </div>
  );
}

function LinkPickerDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (candidate: NotionCandidateDTO) => void;
  linking: boolean;
}) {
  const { open, onOpenChange, onPick, linking } = props;
  const [search, setSearch] = useState("");
  const candidatesQuery = useIntegrationKbCandidates(search.trim() || undefined, open);

  const configured = candidatesQuery.data?.configured ?? true;
  const candidates = candidatesQuery.data?.candidates ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Link a Notion page</DialogTitle>
          <DialogDescription>
            Search the Knowledge Base and pick a page for this partner. You can
            link several.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search pages by title…"
            className="pl-9"
            disabled={!configured}
          />
        </div>

        <div className="max-h-72 space-y-1 overflow-y-auto">
          {!configured ? (
            <p className="rounded-md border border-dashed border-border/70 bg-muted/20 px-3 py-6 text-center text-sm text-muted-foreground">
              Notion isn't configured on the server. Set NOTION_TOKEN and
              NOTION_INTEGRATIONS_DB_ID to enable linking.
            </p>
          ) : candidatesQuery.isLoading ? (
            <div className="space-y-1">
              <Skeleton className="h-10 w-full rounded-md" />
              <Skeleton className="h-10 w-full rounded-md" />
            </div>
          ) : candidatesQuery.isError ? (
            <p className="rounded-md border border-dashed border-border/70 bg-muted/20 px-3 py-6 text-center text-sm text-muted-foreground">
              {candidatesQuery.error?.message ?? "Couldn't load pages."}
            </p>
          ) : candidates.length === 0 ? (
            <p className="rounded-md border border-dashed border-border/70 bg-muted/20 px-3 py-6 text-center text-sm text-muted-foreground">
              {search ? "No pages match your search." : "No pages found."}
            </p>
          ) : (
            candidates.map((candidate) => (
              <button
                key={candidate.id}
                type="button"
                disabled={linking}
                onClick={() => onPick(candidate)}
                className="flex w-full items-center gap-2 rounded-md border border-transparent px-3 py-2 text-left text-sm hover:border-border/70 hover:bg-muted/40 disabled:opacity-50"
              >
                {candidate.icon && !candidate.icon.startsWith("http") ? (
                  <span aria-hidden>{candidate.icon}</span>
                ) : (
                  <BookOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <span className="min-w-0 flex-1 truncate">{candidate.title}</span>
                {candidate.lastEditedTime && (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {formatRelative(candidate.lastEditedTime)}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
